import React, { useRef, useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import ProfilePicture from './ProfilePicture';

const EmployeeSidebar = ({ 
  userProfileImage,
  userName,
  userSubtitle,
  activeMenuItem = "imputations",
  onMenuItemClick,
  onSignOut,
  onProfileClick,
  userInitials,
  isMobileOpen = false,
  onClose,
  showWeeklyView = false,
}) => {
  const { t } = useTranslation('nav');
  const displayName = userName || t('employeeDefault');
  const displaySubtitle = userSubtitle || t('managerSubtitleDefault');
  const menuItems = [
    { id: 'imputations', icon: 'assignment', section: 'main' },
    { id: 'performance-comparison', icon: 'trending_up', section: 'main' },
    { id: 'rewards', icon: 'card_giftcard', section: 'main' },
    { id: 'badge-rules', icon: 'rule', section: 'main' },
    { id: 'team', icon: 'groups', section: 'main' },
    ...(showWeeklyView
      ? [{ id: 'weekly-view', icon: 'calendar_view_week', section: 'main' }]
      : []),
  ];

  const mainItems = menuItems.filter(item => item.section === 'main');
  
  const SIDEBAR_LOCK_KEY = 'employeeSidebarLocked';
  const itemRefs = useRef({});
  const navRef = useRef(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ top: '0px', height: '0px', opacity: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isLocked, setIsLocked] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_LOCK_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const isExpanded = isMobile || isHovered || isLocked;

  const defaultProfileImage = "https://lh3.googleusercontent.com/aida-public/AB6AXuBn46b1j429RzppC5YmhqSuXCegWUsXjZNJ4TPBPm4eWQFJK5D8g07ztPHhHJ9Rti8eTB-jezI0y48SBSicrReDCOlpn1s1Wsqb-5F45A3J39RVe_i7QQ8Rn7OFEO1VpV7Zc2OT_fiwK-7e2Cyuu6lHoApl1a5OM3eQcCi2Hto6pPHOfctfgoPrWU5Q67fP229iKoAhb7fAsHnCe7cx077Qa0fSPzL3tRC1d3tWW4zdwS1YkPdUuz2AerUQ7xFVJpVQssaWMblyP1k";

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleItemClick = (itemId) => {
    if (onMenuItemClick) {
      onMenuItemClick(itemId);
    }
    // Close mobile menu when item is clicked
    if (onClose) {
      onClose();
    }
  };

  const handleSignOut = () => {
    if (onSignOut) {
      onSignOut();
    }
  };

  const toggleLock = () => {
    setIsLocked((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_LOCK_KEY, String(next));
      } catch (_) {}
      return next;
    });
  };

  // Function to update indicator position
  const updateIndicatorPosition = useCallback(() => {
    const activeRef = itemRefs.current[activeMenuItem];
    const navElement = navRef.current;
    if (!activeRef || !navElement) {
      setIndicatorStyle((prev) => ({ ...prev, opacity: 0 }));
      return;
    }

    // Keep the active item visible (esp. Planning & Scheduling as last item)
    activeRef.scrollIntoView({ block: 'nearest', inline: 'nearest' });

    const navRect = navElement.getBoundingClientRect();
    const itemRect = activeRef.getBoundingClientRect();
    const top = itemRect.top - navRect.top + navElement.scrollTop;
    const height = itemRect.height;

    if (height <= 0) {
      setIndicatorStyle((prev) => ({ ...prev, opacity: 0 }));
      return;
    }

    setIndicatorStyle({
      top: `${top}px`,
      height: `${height}px`,
      opacity: 1,
    });
  }, [activeMenuItem]);

  // Recalculate after active item / expand / menu length changes (layout settle)
  useEffect(() => {
    let raf1 = 0;
    let raf2 = 0;
    const timer = setTimeout(() => {
      updateIndicatorPosition();
      raf1 = requestAnimationFrame(() => {
        updateIndicatorPosition();
        raf2 = requestAnimationFrame(updateIndicatorPosition);
      });
    }, 0);
    // Extra pass after enter animations / expand width spring
    const lateTimer = setTimeout(updateIndicatorPosition, 320);
    return () => {
      clearTimeout(timer);
      clearTimeout(lateTimer);
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [activeMenuItem, isExpanded, mainItems.length, showWeeklyView, updateIndicatorPosition]);

  // Update position on scroll / resize of the nav
  useEffect(() => {
    const navElement = navRef.current;
    if (!navElement) return undefined;

    const handleScroll = () => updateIndicatorPosition();
    navElement.addEventListener('scroll', handleScroll);

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => updateIndicatorPosition())
      : null;
    resizeObserver?.observe(navElement);

    return () => {
      navElement.removeEventListener('scroll', handleScroll);
      resizeObserver?.disconnect();
    };
  }, [updateIndicatorPosition]);

  const handleProfileClick = () => {
    if (onProfileClick) {
      onProfileClick();
    }
    // Close mobile menu when profile is clicked
    if (onClose) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      <AnimatePresence>
        {isMobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-40 md:hidden"
              onClick={onClose}
            />
          </>
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{
          x: isMobile && !isMobileOpen ? -288 : 0,
          width: isMobile ? 288 : (isExpanded ? 288 : 72)
        }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="bg-surface border-r border-border flex-col fixed md:relative flex md:flex h-full shadow-sm dark:shadow-[4px_0_24px_rgba(0,0,0,0.25)] z-50 overflow-hidden shrink-0"
        style={{ minWidth: isMobile ? undefined : 72 }}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => !isMobile && setIsHovered(false)}
      >
      <div 
        className={`border-b border-border flex items-center cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${isExpanded ? 'p-6 gap-4' : 'p-3 justify-center'}`}
        onClick={handleProfileClick}
      >
        <div className="relative shrink-0">
          <ProfilePicture
            profilePictureUrl={userProfileImage}
            initials={userInitials || (displayName ? displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'E')}
            size={isExpanded ? "md" : "sm"}
          />
          <div className="absolute bottom-0 right-0 size-3 bg-emerald-500 rounded-full border-2 border-white dark:border-slate-800"></div>
        </div>
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col min-w-0 overflow-hidden flex-1"
            >
              <h1 className="text-primary text-base font-bold leading-tight truncate">{displayName}</h1>
              <p className="text-text-secondary text-xs font-normal truncate">{displaySubtitle}</p>
            </motion.div>
          )}
        </AnimatePresence>
        {!isMobile && (
          <button
            type="button"
            title={isLocked ? t('unlockSidebar') : t('lockSidebar')}
            className="p-1.5 rounded-md text-text-secondary hover:bg-slate-100 dark:hover:bg-slate-600/50 hover:text-primary transition-colors shrink-0 ml-auto"
            onClick={(e) => {
              e.stopPropagation();
              toggleLock();
            }}
          >
            <span className={`material-symbols-outlined text-[20px] block ${isLocked ? 'text-primary' : ''}`}>
              {isLocked ? 'dock_to_right' : 'crop_square'}
            </span>
          </button>
        )}
      </div>
      <nav
        ref={navRef}
        className={`flex-1 overflow-y-auto py-6 flex flex-col gap-2 relative ${isExpanded ? 'px-4' : 'px-2'}`}
      >        {/* Sliding background indicator */}
        <motion.div
          className={`absolute bg-primary rounded-lg shadow-md shadow-blue-900/10 z-0 ${isExpanded ? 'left-4 right-4' : 'left-2 right-2'}`}
          initial={false}
          animate={{
            top: indicatorStyle.top,
            height: indicatorStyle.height,
            opacity: indicatorStyle.opacity,
          }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30,
            mass: 0.5,
          }}
        />
        
        {mainItems.map((item, index) => (
          <motion.a
            key={item.id}
            ref={(el) => (itemRefs.current[item.id] = el)}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`flex items-center gap-3 rounded-lg transition-all cursor-pointer relative z-10 ${
              isExpanded ? 'px-3 py-2.5' : 'px-0 py-2.5 justify-center'
            } ${
              activeMenuItem === item.id
                ? 'text-white'
                : 'text-text-secondary hover:bg-slate-100 dark:hover:bg-slate-600/50 hover:text-primary'
            }`}
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleItemClick(item.id);
            }}
          >
            <motion.span
              className="material-symbols-outlined text-[20px] shrink-0"
              animate={activeMenuItem === item.id ? { scale: [1, 1.1, 1] } : {}}
              transition={{ duration: 0.3 }}
            >
              {item.icon}
            </motion.span>
            <AnimatePresence>
              {isExpanded && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                  className="text-sm font-medium whitespace-nowrap overflow-hidden"
                >
                  {t(`menu.${item.id}`)}
                </motion.span>
              )}
            </AnimatePresence>
          </motion.a>
        ))}
      </nav>
      <div className={`border-t border-border ${isExpanded ? 'p-4' : 'p-3'}`}>
        <button 
          className={`flex items-center rounded-lg text-text-secondary hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/40 dark:hover:text-red-300 transition-colors w-full ${
            isExpanded ? 'gap-3 px-3 py-2' : 'justify-center p-2'
          }`}
          onClick={handleSignOut}
        >
          <span className="material-symbols-outlined text-[20px] shrink-0">logout</span>
          <AnimatePresence>
            {isExpanded && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2 }}
                className="text-sm font-medium whitespace-nowrap overflow-hidden"
              >
                {t('signOut')}
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>
    </motion.aside>
    </>
  );
};

export default EmployeeSidebar;
